import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import type { GroupField as GroupFieldType } from '@certhon/models';
import { ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useListContext } from '../context';
import { Field } from './Field';
import { FieldFeedbackBadge } from '@/features/feedback/components/FieldFeedbackBadge';
import { resolveLocalized, usePreviewLocale } from './PreviewLocaleContext';

interface GroupFieldProps {
  field: GroupFieldType;
  activeFieldId?: string;
  onSelectField?: (fieldId: string) => void;
}

/** Count answered leaf fields within a group's children, recursively. */
function countGroupProgress(field: GroupFieldType): {
  answered: number;
  total: number;
} {
  let answered = 0;
  let total = 0;
  for (const child of field.children) {
    if (child.type === 'GROUP_FIELD') {
      const sub = countGroupProgress(child);
      answered += sub.answered;
      total += sub.total;
    } else if (child.type !== 'ROOT_FIELD') {
      total += 1;
      if (child.value !== null) answered += 1;
    }
  }
  return { answered, total };
}

/**
 * Collapsible group section matching the mock's MockGroupSection.
 *
 * In **edit mode**, renders only the group header (no children or collapsible).
 * Children are rendered as flat items by the parent `EditModeTree` so that a
 * single `SortableContext` can handle all drag-and-drop.
 *
 * In **view / fill-in mode**, renders a full collapsible section with children.
 */
export function GroupField({
  field,
  activeFieldId,
  onSelectField,
}: GroupFieldProps) {
  const [open, setOpen] = useState(true);
  const locale = usePreviewLocale();
  const { mode } = useListContext();
  const isEditMode = mode === 'edit';

  const title = resolveLocalized(field.title_nl, field.title_en, locale);
  const { answered, total } = countGroupProgress(field);

  // In edit mode, render just the header -- children are rendered flat
  // by EditModeTree in FieldTree.tsx.
  if (isEditMode) {
    return (
      <div
        className="bg-muted/60 hover:bg-muted flex w-full items-center gap-1.5 border-b px-3 py-2.5
          text-left"
        role="button"
        tabIndex={0}
        onClick={() => onSelectField?.(field.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelectField?.(field.id);
          }
        }}
      >
        <span className="flex-1 text-sm font-semibold tracking-tight">
          {title || '(untitled group)'}
        </span>
        <FieldFeedbackBadge fieldId={field.id} size="small" />
        <span className="text-muted-foreground text-xs tabular-nums">
          {answered}/{total}
        </span>
      </div>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      {/* Group header -- visually distinct from leaf rows */}
      <CollapsibleTrigger
        className="bg-muted/60 hover:bg-muted flex w-full items-center gap-1.5 border-b px-3 py-2.5
          text-left"
      >
        <ChevronRight
          className={cn(
            'text-muted-foreground h-4 w-4 shrink-0 transition-transform duration-200',
            open && 'rotate-90',
          )}
        />
        <span className="flex-1 text-sm font-semibold tracking-tight">
          {title || ''}
        </span>
        <FieldFeedbackBadge fieldId={field.id} size="small" />
        <span className="text-muted-foreground text-xs tabular-nums">
          {answered}/{total}
        </span>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="divide-border divide-y">
          {field.children.map((child, index) => (
            <Field
              key={child.id}
              field={child}
              index={index}
              activeFieldId={activeFieldId}
              onSelectField={onSelectField}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
